import React, { useCallback, useRef, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { view } from '@yola/ws-sdk';
import { designSystem } from '@yola/ws-ui';
import useEventListener from 'src/js/modules/utils/custom-hooks/use-event-listener';
import dropdownEvents from 'src/js/modules/common/constants/dropdown-events';
import getElementAncestorChain from '../helpers/get-element-ancestor-chain';
import { getLabelWidthParts } from '../helpers/get-label-trigger-width';

const { Dropdown, DropdownTrigger, DropdownContent, ActionList, ActionListItem, ChevronButton } =
  designSystem;

const SPACING_BETWEEN_LABEL_AND_DROPDOWN = 12;

const LayerSelectTrigger = ({ elementId, hideHighlighter }) => {
  const [isOpen, setIsOpen] = useState(false);

  const liveElement = view.accessors.getLiveElement(elementId);
  const chain = getElementAncestorChain(liveElement);
  const dispatch = useDispatch();
  const focusedElementId = useSelector(view.selectors.getFocusedElement);
  const currentFocusedElement = view.accessors.getLiveElement(focusedElementId);

  const layerSelectTriggerRef = useRef(null);

  const offsetCrossAxis = useMemo(() => {
    const elementLabel = chain.find((item) => item.elementId === elementId)?.label || '';
    const { triggerInnerPadding, labelTextWidth, labelPaddingRight } = getLabelWidthParts(
      elementLabel,
      false,
      true
    );

    return (
      (triggerInnerPadding +
        labelTextWidth +
        labelPaddingRight +
        SPACING_BETWEEN_LABEL_AND_DROPDOWN) *
      -1
    );
  }, [chain, elementId]);

  const handleChange = useCallback(
    (value) => {
      hideHighlighter();

      if (value === elementId) {
        setIsOpen(false);
        Dropdown.hide();
        return;
      }

      dispatch(view.actions.setSelectedElement(value, { forced: true }));

      if (currentFocusedElement) {
        dispatch(view.actions.blurFocusedElement({ preserveFocusedTextElement: true }));
      }
    },
    [dispatch, currentFocusedElement, elementId, hideHighlighter]
  );

  const handleHide = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleShow = useCallback(() => {
    setIsOpen(true);
  }, []);

  useEventListener(dropdownEvents.HIDE, handleHide);

  return (
    <div className="ws-layer-select-trigger" ref={layerSelectTriggerRef}>
      <Dropdown
        placement="bottom-left"
        portalRootNode={layerSelectTriggerRef.current}
        offsetCrossAxis={offsetCrossAxis}
        onHide={handleHide}
        onShow={handleShow}
      >
        <DropdownTrigger>
          <ChevronButton direction={isOpen ? 'up' : 'down'} />
        </DropdownTrigger>
        <DropdownContent>
          <ActionList withMobileBorder>
            {chain.map(({ elementId: itemId, label }) => (
              <ActionListItem
                key={itemId}
                title={label}
                selected={itemId === elementId}
                onClick={() => handleChange(itemId)}
              />
            ))}
          </ActionList>
        </DropdownContent>
      </Dropdown>
    </div>
  );
};

LayerSelectTrigger.propTypes = {
  elementId: PropTypes.string.isRequired,
  hideHighlighter: PropTypes.func.isRequired,
};

export default LayerSelectTrigger;
